import { useCallback, useEffect, useMemo, useRef, useState } from "react"

// Extensions are explicit: this is a .js file and the root package.json sets
// "type": "module", so resolution here is strict ESM.
import api from "../../../api.js"
import { haversineMeters } from "../../../utils/geo.js"
import { enqueue, isOnline } from "../lib/outbox.js"

// ── Fix quality ──────────────────────────────────────────────────────────────
// Above this the fix is too vague to move the marker on its own.
const MAX_ACCEPTABLE_ACCURACY_M = 75
// ...unless the driver clearly moved anyway, which outweighs a pessimistic
// accuracy estimate (common indoors and in urban canyons).
const BIG_MOVE_DESPITE_BAD_ACCURACY_M = 25
// Below this the fix isn't trustworthy enough to route from at all.
const MAX_ROUTING_ACCURACY_M = 150

// ── Cadence ──────────────────────────────────────────────────────────────────
// One scheduler drives every sink. Each profile is {tracker, flush} in ms.
//
// The old code ran three hand-rolled ref throttles inside the watch callback at
// fixed 3s/12s/30s intervals regardless of what the vehicle was doing, so a van
// parked for an hour produced the same write volume as one crossing the city.
const CADENCE = {
  // Close to the drop-off: tighten everything so the proximity gate and the
  // dispensary's view react quickly.
  approach: { tracker: 5000, flush: 10000 },
  // Normal driving.
  moving: { tracker: 15000, flush: 20000 },
  // Stationary. The stability filter already discards non-moves, so there is
  // usually nothing to flush; the tracker still beats occasionally so dispatch
  // can tell the difference between "parked" and "app died".
  idle: { tracker: 60000, flush: 60000 },
}

const APPROACH_RADIUS_M = 300
const MOVING_SPEED_MPS = 1.0

const SCHEDULER_TICK_MS = 1000
// Bounded so a long offline stretch can't grow the buffer without limit; the
// server accepts 200 points per batch.
const MAX_BUFFERED_POINTS = 200

function selectCadence({ speed, distanceToDestination }) {
  if (distanceToDestination != null && distanceToDestination <= APPROACH_RADIUS_M) {
    return CADENCE.approach
  }
  if (speed != null && speed >= MOVING_SPEED_MPS) return CADENCE.moving
  return CADENCE.idle
}

/**
 * Owns the driver's GPS lifecycle for one stop: a single watchPosition, the
 * stability filter, and one scheduler that decides when to push to the AWS
 * tracker and when to flush buffered breadcrumbs.
 *
 * @param {object}  params
 * @param {string}  params.jobId        payment id of the stop being driven
 * @param {object}  params.destination  {lat, lng} for cadence + distance readout
 * @param {boolean} params.enabled      set false to stop watching (e.g. after completion)
 */
export function useDriverTelemetry({ jobId, destination, enabled = true }) {
  const [coords, setCoords] = useState(null)
  const [heading, setHeading] = useState(null)
  const [meta, setMeta] = useState(null)
  const [error, setError] = useState(null)
  const [pingFailed, setPingFailed] = useState(false)

  // Buffered breadcrumbs awaiting a flush.
  const bufferRef = useRef([])
  // Last point accepted by the stability filter — the comparison baseline.
  const lastStableRef = useRef(null)
  const lastTrackerSentRef = useRef(0)
  const lastFlushRef = useRef(0)
  const flushInFlightRef = useRef(false)

  // The scheduler reads these without wanting to restart on every change.
  const latestRef = useRef({ coords: null, speed: null, distanceToDestination: null })

  const destLat = destination?.lat
  const destLng = destination?.lng

  const distanceToDestination = useMemo(() => {
    if (!coords || destLat == null || destLng == null) return null
    const meters = haversineMeters(coords, { lat: destLat, lng: destLng })
    return Number.isFinite(meters) ? meters : null
  }, [coords, destLat, destLng])

  useEffect(() => {
    latestRef.current = {
      coords,
      speed: meta?.speed ?? null,
      distanceToDestination,
    }
  }, [coords, meta?.speed, distanceToDestination])

  // ── Flush ──────────────────────────────────────────────────────────────────
  const flushBreadcrumbs = useCallback(async () => {
    if (!jobId) return
    if (flushInFlightRef.current) return
    if (bufferRef.current.length === 0) return

    // Take the batch optimistically; put it back on failure so nothing is lost.
    const batch = bufferRef.current
    bufferRef.current = []
    flushInFlightRef.current = true

    const url = `/api/driver/plan/today/stops/${jobId}/location/batch`

    try {
      if (!isOnline()) {
        // Hand the batch to the outbox rather than losing the trip history.
        await enqueue({ url, body: { points: batch }, kind: "breadcrumbs" })
        setPingFailed(false)
        return
      }

      await api.post(url, { points: batch })
      setPingFailed(false)
    } catch (e) {
      console.error("[useDriverTelemetry] breadcrumb flush:", e)

      const status = e?.response?.status ?? e?.status ?? null
      const rejected = status != null && status >= 400 && status < 500

      if (rejected) {
        // The server refused these points (stop no longer in progress, say).
        // Replaying them would fail identically, so drop them.
        setPingFailed(true)
      } else {
        // Unreachable — persist so a reload or crash doesn't lose the path.
        try {
          await enqueue({ url, body: { points: batch }, kind: "breadcrumbs" })
          setPingFailed(false)
        } catch (queueErr) {
          console.error("[useDriverTelemetry] breadcrumb enqueue:", queueErr)
          // Last resort: keep them in memory and trim from the oldest end.
          bufferRef.current = [...batch, ...bufferRef.current].slice(-MAX_BUFFERED_POINTS)
          setPingFailed(true)
        }
      }
    } finally {
      flushInFlightRef.current = false
    }
  }, [jobId])

  // ── Tracker ────────────────────────────────────────────────────────────────
  const pushToTracker = useCallback(async (point) => {
    try {
      await api.post("/api/driver/location", {
        lat: point.lat,
        lng: point.lng,
        accuracy: point.accuracy ?? null,
      })
      setError(null)
    } catch (e) {
      setError(e?.message || "Failed to update GPS location")
    }
  }, [])

  // ── Watch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !jobId) return undefined

    if (!navigator.geolocation) {
      setError("Geolocation is not supported on this device")
      return undefined
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos?.coords?.latitude
        const lng = pos?.coords?.longitude
        if (lat == null || lng == null) return

        const now = Date.now()
        const accuracyRaw = pos?.coords?.accuracy
        const accuracy = accuracyRaw != null ? Number(accuracyRaw) : null
        const speedRaw = pos?.coords?.speed
        const speed = speedRaw != null && Number.isFinite(Number(speedRaw)) ? Number(speedRaw) : null
        const headingRaw = pos?.coords?.heading

        setMeta({ accuracy, speed, updatedAt: now })
        if (headingRaw != null && Number.isFinite(Number(headingRaw))) {
          setHeading(Number(headingRaw))
        }

        const prev = lastStableRef.current
        const next = { lat, lng, accuracy, recordedAt: new Date(now).toISOString() }

        // Always accept the first fix so the marker renders even on a coarse
        // indoor/Wi-Fi position.
        if (!prev) {
          lastStableRef.current = next
          setCoords({ lat, lng })
          bufferRef.current.push(next)
          return
        }

        const movedMeters = haversineMeters(prev, { lat, lng })

        // Reject vague fixes unless they show real movement.
        if (
          Number.isFinite(accuracy) &&
          accuracy > MAX_ACCEPTABLE_ACCURACY_M &&
          movedMeters < BIG_MOVE_DESPITE_BAD_ACCURACY_M
        ) {
          return
        }

        // Scale the movement gate to the fix's own error so a noisy signal
        // doesn't jitter the marker while parked.
        const minMoveMeters = Number.isFinite(accuracy)
          ? Math.max(3, Math.min(10, accuracy * 0.5))
          : 8

        if (movedMeters < minMoveMeters) return

        lastStableRef.current = next
        setCoords({ lat, lng })

        bufferRef.current.push(next)
        if (bufferRef.current.length > MAX_BUFFERED_POINTS) {
          bufferRef.current = bufferRef.current.slice(-MAX_BUFFERED_POINTS)
        }
      },
      (err) => {
        setError(err?.message || "Failed to read GPS location")
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    )

    return () => {
      try {
        navigator.geolocation.clearWatch(watchId)
      } catch {
        // ignore
      }
    }
  }, [enabled, jobId])

  // ── Scheduler ──────────────────────────────────────────────────────────────
  // One timer decides all upload timing, so cadence lives in one place instead
  // of being spread across throttles in the watch callback.
  useEffect(() => {
    if (!enabled || !jobId) return undefined

    const interval = setInterval(() => {
      const { coords: current, speed, distanceToDestination: distance } = latestRef.current
      const cadence = selectCadence({ speed, distanceToDestination: distance })
      const now = Date.now()

      if (current && now - lastTrackerSentRef.current >= cadence.tracker) {
        lastTrackerSentRef.current = now
        pushToTracker({
          lat: current.lat,
          lng: current.lng,
          accuracy: lastStableRef.current?.accuracy ?? null,
        })
      }

      if (now - lastFlushRef.current >= cadence.flush) {
        lastFlushRef.current = now
        flushBreadcrumbs()
      }
    }, SCHEDULER_TICK_MS)

    return () => clearInterval(interval)
  }, [enabled, jobId, pushToTracker, flushBreadcrumbs])

  // Best-effort flush when the app is backgrounded or closed, so the tail of a
  // trip isn't lost on the way to the completion screen.
  useEffect(() => {
    if (!enabled || !jobId) return undefined

    const onHidden = () => {
      if (document.visibilityState === "hidden") flushBreadcrumbs()
    }

    document.addEventListener("visibilitychange", onHidden)
    window.addEventListener("pagehide", flushBreadcrumbs)

    return () => {
      document.removeEventListener("visibilitychange", onHidden)
      window.removeEventListener("pagehide", flushBreadcrumbs)
      flushBreadcrumbs()
    }
  }, [enabled, jobId, flushBreadcrumbs])

  const accuracy = meta?.accuracy ?? null

  return {
    coords,
    heading,
    accuracy,
    speed: meta?.speed ?? null,
    updatedAt: meta?.updatedAt ?? null,
    error,
    pingFailed,
    distanceToDestination,
    /** True when the fix is good enough to recalculate a route from. */
    canRouteFromFix:
      Boolean(coords) && Number.isFinite(accuracy) && accuracy <= MAX_ROUTING_ACCURACY_M,
    /** Force an immediate breadcrumb flush (e.g. just before completing). */
    flushNow: flushBreadcrumbs,
  }
}

export default useDriverTelemetry
